import React, { useEffect, useMemo, useState } from 'react';
import { BrushawayHeader } from '../../../../molecules/Brushaway/BrushawayHeader';
import { BrushawayBody } from '../../../../molecules/Brushaway/BrushawayBody';
import { BrushawayFooter } from '../../../../molecules/Brushaway/BrushawayFooter';
import { useBrushaway } from '../../../../molecules/Brushaway/BrushawayContext';
import { Button } from '../../../../atoms/Button/Button';
import { Text } from '../../../../atoms/Text/Text';
import { ProgressBar } from '../../../../molecules/ProgressBar/ProgressBar';
import { ConfirmDialog } from '../../../../molecules/ConfirmDialog/ConfirmDialog';
import { FileHandler } from '../../../../molecules/FileHandler/FileHandler';
import { RejectedFilesAlert } from '../RejectedFilesAlert';
import { QueueTable } from '../QueueTable';
import { formatBytes, isRetryable } from '../../utils';
let rejectedFileIdCounter = 0;
function nextRejectedFileId() {
    rejectedFileIdCounter += 1;
    return `rejected-${rejectedFileIdCounter}`;
}
/**
 * Rendered as `<Brushaway>`'s children -- the only place in this organism with access to
 * `useBrushaway()`, needed for the discard-confirm reopen trick (see the parent
 * `AddAttachmentsBrushaway.tsx`'s module doc).
 */
export function AddAttachmentsBrushawayPanel({ items, isUploading, aggregateProgress, onFilesSelected, onNameChange, onRemoveItem, onRetryItem, onRetryFailed, onCancelUpload, onSubmit, maxSizeBytes, accept, columns, labels, discardConfirmOpen, onCancelDiscard, onConfirmDiscard, onRequestClose }) {
    var _a;
    const { openBrushaway, isOpen } = useBrushaway();
    const [rejectedFiles, setRejectedFiles] = useState([]);
    // Drop client-side rejections when the drawer closes. `Brushaway` renders its children
    // unconditionally and only toggles `display`, so this panel never unmounts and its state
    // survives a close — a size-limit alert stayed on screen the next time the drawer was opened,
    // long after the files it named were gone (IP-16281 issue 3).
    //
    // Keyed on the drawer's own open state rather than a close handler because `Brushaway`'s
    // built-in X and its Escape key both close it without routing through this organism's
    // choreography, so a handler-based reset would miss exactly the paths users take.
    useEffect(() => {
        if (!isOpen)
            setRejectedFiles([]);
    }, [isOpen]);
    // Staged-file removal is confirmed (IP-16281 issue 1): the trash icon used to drop the file
    // immediately, with the queue as the only record of what had been added.
    const [pendingRemoveId, setPendingRemoveId] = useState(null);
    const pendingRemove = (_a = items.find((item) => item.id === pendingRemoveId)) !== null && _a !== void 0 ? _a : null;
    useEffect(() => {
        if (!isOpen)
            setPendingRemoveId(null);
    }, [isOpen]);
    // `reason` is carried, not discarded. It used to be bound as `_reason`, which meant every
    // rejection rendered the SIZE copy — a wrong-type file was told it was "over the N MB limit".
    const handleReject = (files, reason) => {
        setRejectedFiles((prev) => [
            ...prev,
            ...files.map((file) => ({
                id: nextRejectedFileId(),
                name: file.name,
                sizeLabel: formatBytes(file.size),
                reason
            }))
        ]);
    };
    // FileHandler's own `items` prop only needs to be non-empty to invoke `renderList` -- the queue
    // table below renders from this component's own `items` closure (the richer
    // `AddAttachmentsQueueItem` shape), not from the `FileHandlerItem[]` `renderList` hands back.
    const fileHandlerItems = useMemo(() => items.map((item) => ({ id: item.id, name: item.name, typeLabel: item.typeLabel })), [items]);
    const total = items.length;
    const doneCount = items.filter((item) => item.status === 'done').length;
    const failedCount = items.filter((item) => isRetryable(item.status)).length;
    const pendingCount = items.filter((item) => item.status === 'pending').length;
    const totalBytes = items.reduce((sum, item) => sum + item.byteSize, 0);
    const handleCancelDiscard = () => {
        onCancelDiscard();
        // Brushaway's own close button already hid the panel (its `closeBrushaway` runs
        // unconditionally) before we got a chance to veto it -- bring it back now that the user chose
        // to keep the queue.
        openBrushaway();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(BrushawayHeader, { title: labels.title, closeLabel: labels.closeLabel }),
        React.createElement(BrushawayBody, null,
            React.createElement(RejectedFilesAlert, { files: rejectedFiles, labels: labels, onDismiss: () => setRejectedFiles([]) }),
            React.createElement(FileHandler, { disabled: isUploading, maxSize: maxSizeBytes, accept: accept, emptyText: labels.emptyText, dropzoneNote: labels.dropzoneNote, items: fileHandlerItems, onFilesSelected: onFilesSelected, onReject: handleReject, renderList: () => (React.createElement(QueueTable, { items: items, isUploading: isUploading, columns: columns, labels: labels, onNameChange: onNameChange, onRetryItem: onRetryItem, onRemoveItem: setPendingRemoveId })) })),
        React.createElement(BrushawayFooter, null,
            total > 0 && (React.createElement(Text, { variant: "x-small", className: "qmb-ui-add-attachments-brushaway__footer-totals" }, labels.footerTotals(total, formatBytes(totalBytes)))),
            isUploading && (React.createElement(React.Fragment, null,
                React.createElement(Text, { variant: "x-small" }, labels.uploadingSummary(doneCount, total, Math.round(aggregateProgress))),
                React.createElement(ProgressBar, { value: aggregateProgress, showLabel: false }),
                React.createElement(Button, { variant: "secondary", onClick: onCancelUpload }, labels.cancelUploadLabel))),
            !isUploading && failedCount > 0 && (React.createElement(React.Fragment, null,
                React.createElement(Text, { variant: "x-small" }, labels.uploadedSummary(doneCount, total, failedCount)),
                React.createElement(Button, { variant: "primary", onClick: onRetryFailed }, labels.retryFailedLabel))),
            !isUploading && failedCount === 0 && (React.createElement(React.Fragment, null,
                React.createElement(Button, { onClick: onRequestClose }, labels.cancelActionLabel),
                React.createElement(Button, { variant: "primary", disabled: pendingCount === 0, onClick: onSubmit }, labels.addButtonLabel(pendingCount))))),
        React.createElement(ConfirmDialog, { isOpen: pendingRemove !== null, title: labels.removeConfirmTitle, description: pendingRemove
                ? labels.removeConfirmDescription(pendingRemove.extension ? `${pendingRemove.name}.${pendingRemove.extension}` : pendingRemove.name)
                : '', confirmLabel: labels.removeConfirmConfirmLabel, confirmVariant: "danger", cancelLabel: labels.removeConfirmCancelLabel, onConfirm: () => {
                if (pendingRemove)
                    onRemoveItem(pendingRemove.id);
                setPendingRemoveId(null);
            }, onCancel: () => setPendingRemoveId(null) }),
        React.createElement(ConfirmDialog, { isOpen: discardConfirmOpen, title: labels.discardConfirmTitle, description: labels.discardConfirmDescription, confirmLabel: labels.discardConfirmConfirmLabel, confirmVariant: "danger", cancelLabel: labels.discardConfirmCancelLabel, onConfirm: onConfirmDiscard, onCancel: handleCancelDiscard })));
}
